import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { KpiCard } from '@/components/reports/KpiCard';
import { ShieldAlert, AlertTriangle, Copy, Archive } from 'lucide-react';

const GAP_LABELS = {
  client: 'Client name',
  owner: 'Project lead',
  resource: 'Assigned resource',
  stage: 'Project stage',
  milestone: 'Milestones',
  wo: 'Work Order number',
  financial: 'Financial data',
  client_contact: 'Client contact',
};

export default function DataQualityPage() {
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get('/reports/kra/data-quality');
      setPayload(r.data);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error(parseErr(e));
    }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useAutoRefetch(load, { collections: ['master_projects', 'work_orders'] });

  const cards = payload?.cards || [];
  const lists = payload?.lists || {};

  return (
    <AppShell>
      <div className="space-y-4" data-testid="data-quality-page">
        <div className="flex items-center gap-2">
          <ShieldAlert size={20} className="text-[hsl(var(--danger))]" />
          <div>
            <h1 className="text-[22px] font-semibold" data-testid="dq-title">Data Quality</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">
              Owner-only. Flags projects whose data can&apos;t drive the reports cleanly. Click through, fix at source, re-run.
            </p>
          </div>
        </div>

        {busy && !payload && !forbidden && (
          <div className="text-center py-16 text-[hsl(var(--foreground-muted))]">Loading…</div>
        )}

        {forbidden && (
          <div className="border rounded-xl p-8 text-center bg-[hsl(0_90%_98%)]"
               style={{ borderColor: 'hsl(0_60%_78%)' }}
               data-testid="dq-forbidden">
            <ShieldAlert size={28} className="mx-auto mb-2 text-[hsl(var(--danger))]" />
            <h2 className="text-[16px] font-semibold text-[hsl(0_60%_35%)]">Owner-only view</h2>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1">
              Data Quality flags are restricted to the studio Owner. Ask them to review the alerts you&apos;re chasing, or head back to Reports.
            </p>
            <Link to="/reports" className="text-[12.5px] text-[hsl(var(--primary))] hover:underline inline-block mt-3">
              ← Back to Reports
            </Link>
          </div>
        )}

        {payload && !forbidden && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5"
                 data-testid="dq-cards">
              {cards.map((c, i) => (
                <KpiCard key={`${c.label}-${i}`} card={c}
                         testId={`dq-card-${i}`} />
              ))}
            </div>

            {lists.missing_fields && (
              <div>
                <h2 className="text-[16px] font-semibold mt-4 mb-2">Missing fields</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3"
                     data-testid="dq-missing-fields">
                  {Object.entries(lists.missing_fields).map(([gap, ids]) => (
                    <MissingFieldList key={gap} gap={gap} ids={ids} />
                  ))}
                </div>
              </div>
            )}

            {(lists.duplicate_projects?.length > 0 || lists.duplicate_wo?.length > 0) && (
              <div>
                <h2 className="text-[16px] font-semibold mt-4 mb-2 flex items-center gap-1.5">
                  <Copy size={15} /> Duplicates
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <DuplicateList title="Project name × Client" items={lists.duplicate_projects}
                                 keyFmt={(k) => `"${k[0]}" · ${k[1]}`}
                                 testId="dq-dup-projects" />
                  <DuplicateList title="Work Order numbers" items={lists.duplicate_wo}
                                 keyFmt={(w) => w.wo_number}
                                 asWo
                                 testId="dq-dup-wo" />
                </div>
              </div>
            )}

            {lists.wo_pointing_to_missing?.length > 0 && (
              <div>
                <h2 className="text-[16px] font-semibold mt-4 mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={15} className="text-[hsl(var(--danger))]" />
                  WOs pointing to missing Master Projects
                </h2>
                <div className="border rounded-xl bg-[hsl(var(--card))]"
                     style={{ borderColor: 'hsl(var(--border))' }}
                     data-testid="dq-wo-orphans">
                  <table className="tbl">
                    <thead><tr><th>WO Number</th><th>Client</th><th>Missing MP id</th></tr></thead>
                    <tbody>
                      {lists.wo_pointing_to_missing.map((w, i) => (
                        <tr key={w.id || i}>
                          <td className="font-mono text-[11.5px]">{w.wo_number || w.work_order_number || '—'}</td>
                          <td>{w.client || '—'}</td>
                          <td className="font-mono text-[10.5px] text-[hsl(var(--foreground-subtle))]">
                            {w.master_project_id}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {lists.archive_candidates?.length > 0 && (
              <div>
                <h2 className="text-[16px] font-semibold mt-4 mb-2 flex items-center gap-1.5">
                  <Archive size={15} /> Archive candidates (older than 12 months, no activity in 90 days)
                </h2>
                <div className="border rounded-xl bg-[hsl(var(--card))]"
                     style={{ borderColor: 'hsl(var(--border))' }}
                     data-testid="dq-archive-candidates">
                  <table className="tbl">
                    <thead><tr><th>Project</th><th>Created</th><th>Last activity</th><th></th></tr></thead>
                    <tbody>
                      {lists.archive_candidates.map(a => (
                        <tr key={a.id}>
                          <td className="font-medium">
                            <Link to={`/projects/${a.id}`}
                                  className="text-[hsl(var(--primary))] hover:underline">
                              {a.project_name}
                            </Link>
                          </td>
                          <td className="text-[11.5px] text-[hsl(var(--foreground-muted))] font-mono">
                            {(a.created_at || '').slice(0, 10)}
                          </td>
                          <td className="text-[11.5px] text-[hsl(var(--foreground-muted))] font-mono">
                            {(a.last_activity || '—').slice(0, 10)}
                          </td>
                          <td className="text-right">
                            <Link to={`/projects/${a.id}`}
                                  className="text-[11px] text-[hsl(var(--primary))] hover:underline">
                              Review →
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function MissingFieldList({ gap, ids }) {
  return (
    <div className="border rounded-xl p-3 bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`dq-missing-${gap}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[13px] font-semibold">Missing {GAP_LABELS[gap] || gap}</div>
        <span className="chip">{ids.length}</span>
      </div>
      {ids.length === 0 && (
        <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))]">Nothing missing.</div>
      )}
      <ul className="space-y-1 max-h-[220px] overflow-y-auto">
        {ids.slice(0, 30).map(pid => (
          <li key={pid} className="text-[12px] flex items-center justify-between gap-2">
            <span className="font-mono text-[10.5px] text-[hsl(var(--foreground-subtle))]">
              {pid.slice(0, 8)}…
            </span>
            <Link to={`/projects/${pid}`}
                  className="text-[11px] text-[hsl(var(--primary))] hover:underline whitespace-nowrap">
              Fix →
            </Link>
          </li>
        ))}
        {ids.length > 30 && (
          <li className="text-[10.5px] text-[hsl(var(--foreground-muted))]">
            + {ids.length - 30} more
          </li>
        )}
      </ul>
    </div>
  );
}

function DuplicateList({ title, items, keyFmt, asWo, testId }) {
  return (
    <div className="border rounded-xl p-3 bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[13px] font-semibold">{title}</div>
        <span className="chip">{items?.length || 0}</span>
      </div>
      {(!items || items.length === 0) && (
        <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))]">No duplicates found.</div>
      )}
      <ul className="space-y-2">
        {(items || []).map((d, i) => (
          <li key={i} className="border rounded-lg p-2"
              style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="text-[12px] font-semibold truncate">
              {keyFmt(asWo ? d : d.key)}
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {(d.ids || []).map(pid => (
                <Link key={pid} to={`/projects/${pid}`}
                      className="text-[10.5px] font-mono text-[hsl(var(--primary))] hover:underline">
                  {pid.slice(0, 8)}
                </Link>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
